import { forwardRef, useState, type InputHTMLAttributes } from 'react'
import { Eye, EyeOff } from 'lucide-react'
import { cn } from '@/lib/cn'

/**
 * A password field with a reveal toggle.
 *
 * People mistype passwords, and on a POS the keyboard is often a cheap one on
 * a counter under bad light. Without a way to look, the only feedback is a
 * failed sign-in — which is indistinguishable from a wrong password, a locked
 * account, or a server being down.
 *
 * Details that matter:
 *
 * `type="button"`. Inside a form, a bare <button> submits, so the toggle would
 * post a half-filled signup every time somebody checked their typing.
 *
 * The autoComplete prop is passed straight through and never overridden.
 * Setting autoComplete="off" here to "be safe" is the classic mistake: it
 * breaks password managers, and the passwords people then invent unaided are
 * far worse than the ones a manager would have stored.
 *
 * The icon shows the ACTION, not the state — an eye means "show", a crossed
 * eye means "hide". Both readings exist in the wild and neither is
 * unambiguous, which is why the aria-label says it in words.
 */
export const PasswordInput = forwardRef<
  HTMLInputElement,
  Omit<InputHTMLAttributes<HTMLInputElement>, 'type'>
>(function PasswordInput({ className, ...props }, ref) {
  const [visible, setVisible] = useState(false)

  return (
    <div className="relative">
      <input
        ref={ref}
        type={visible ? 'text' : 'password'}
        className={cn(
          'h-10 w-full rounded-lg bg-surface pl-3 text-sm text-ink',
          // Room for the button, so a long password does not run under it.
          'pr-11',
          'ring-1 ring-border-strong transition-shadow',
          'focus:outline-none focus:ring-2 focus:ring-brand',
          'disabled:cursor-not-allowed disabled:bg-sunken disabled:text-ink-muted',
          'aria-[invalid=true]:ring-critical',
          className,
        )}
        {...props}
      />

      <button
        type="button"
        onClick={() => setVisible((current) => !current)}
        // Said in words. An eye icon alone is genuinely ambiguous about
        // whether it shows the current state or the action.
        aria-label={visible ? 'Hide password' : 'Show password'}
        aria-pressed={visible}
        // Focusable, deliberately: a keyboard user who cannot see the field
        // needs this more than anyone, and skipping it in the tab order to
        // save one keypress takes it away from them.
        className={cn(
          'absolute right-1 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center',
          'rounded-md text-ink-muted transition-colors',
          'hover:bg-sunken hover:text-ink-secondary',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand',
        )}
      >
        {visible ? (
          <EyeOff className="h-4 w-4" strokeWidth={1.75} aria-hidden />
        ) : (
          <Eye className="h-4 w-4" strokeWidth={1.75} aria-hidden />
        )}
      </button>
    </div>
  )
})
